import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import pagosOficinaService from '../services/pagosOficinaService';
import NotificationModal from '../components/NotificationModal';
import ConfirmModal from '../components/ConfirmModal';
import './PagosOficina.css';

const PagosOficina = () => {
  const { user } = useAuth();
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [selectedPago, setSelectedPago] = useState(null);
  const [bcvRates, setBcvRates] = useState({ USD: 250, EUR: 280 }); // Tasas de fallback
  const [loadingRates, setLoadingRates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pagoToDelete, setPagoToDelete] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', message: '' });
  const [formData, setFormData] = useState({
    cedula: '',
    monto_bs: '',
    monto_usd: '',
    monto_eur: '',
    fecha: new Date().toISOString().split('T')[0], // Fecha actual
    concepto: '',
    referencia: '',
    descripcion: ''
  });
  const [filters, setFilters] = useState({
    search: '',
    fecha_desde: '',
    fecha_hasta: '',
    concepto: '',
    page: 1,
    limit: 50
  });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadPagos();
    loadBCVRates();
  }, [filters]);

  const loadBCVRates = async () => {
    setLoadingRates(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bcv/rates`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setBcvRates(result.data);
          console.log('✅ Tasas BCV cargadas:', result.data);
        }
      }
    } catch (error) {
      console.error('Error cargando tasas BCV:', error);
    } finally {
      setLoadingRates(false);
    }
  };

  // Función para convertir automáticamente a EUR
  const convertirAEUR = (monto, moneda) => {
    if (!monto || monto <= 0) return 0;
    
    const valor = parseFloat(monto);
    if (moneda === 'EUR') return valor;
    if (moneda === 'USD') {
      // USD a EUR: (valor USD * tasa USD en Bs) / tasa EUR en Bs
      return (valor * bcvRates.USD) / bcvRates.EUR;
    }
    if (moneda === 'BS') {
      // Bs a EUR: valor Bs / tasa EUR en Bs
      return valor / bcvRates.EUR;
    }
    
    return 0;
  };

  // Manejar cambios en campos de moneda
  const handleMonedaChange = (campo, valor) => {
    const newFormData = { ...formData, [campo]: valor };
    
    // Si el campo está vacío, limpiar EUR
    if (!valor || valor === '' || valor === '0') {
      if (campo === 'monto_bs') {
        newFormData.monto_eur = '';
        newFormData.monto_usd = '';
      } else if (campo === 'monto_usd') {
        newFormData.monto_eur = '';
        newFormData.monto_bs = '';
      } else if (campo === 'monto_eur') {
        newFormData.monto_bs = '';
        newFormData.monto_usd = '';
      }
    } else {
      // Convertir automáticamente a EUR
      if (campo === 'monto_bs') {
        const eurEquivalente = convertirAEUR(valor, 'BS');
        newFormData.monto_eur = eurEquivalente.toFixed(2);
        newFormData.monto_usd = '';
      } else if (campo === 'monto_usd') {
        const eurEquivalente = convertirAEUR(valor, 'USD');
        newFormData.monto_eur = eurEquivalente.toFixed(2);
        newFormData.monto_bs = '';
      } else if (campo === 'monto_eur') {
        // Si se introduce EUR directamente, limpiar otros campos
        newFormData.monto_bs = '';
        newFormData.monto_usd = '';
      }
    }
    
    setFormData(newFormData);
  };

  const loadPagos = async () => {
    setLoading(true);
    try {
      const data = await pagosOficinaService.getPagos(filters);
      setPagos(data.data || []);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error cargando pagos:', error);
      setNotification({
        show: true,
        type: 'error',
        title: 'Error al Cargar',
        message: 'No se pudieron cargar los pagos de oficina'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Limpiar datos antes de enviar - convertir strings vacíos a null para campos opcionales
      const dataToSend = {
        ...formData,
        monto_bs: formData.monto_bs === '' ? null : parseFloat(formData.monto_bs),
        monto_usd: formData.monto_usd === '' ? null : parseFloat(formData.monto_usd),
        monto_eur: parseFloat(formData.monto_eur),
        referencia: formData.referencia === '' ? null : formData.referencia,
        descripcion: formData.descripcion === '' ? null : formData.descripcion
      };
      
      if (modalMode === 'create') {
        await pagosOficinaService.createPago(dataToSend);
        setNotification({
          show: true,
          type: 'success',
          title: 'Pago Creado',
          message: 'El pago de oficina se registró exitosamente'
        });
      } else {
        await pagosOficinaService.updatePago(selectedPago.id, dataToSend);
        setNotification({
          show: true,
          type: 'success',
          title: 'Pago Actualizado',
          message: 'El pago de oficina se actualizó exitosamente'
        });
      }
      
      setShowModal(false);
      resetForm();
      loadPagos();
    } catch (error) {
      console.error('Error:', error);
      
      let errorMessage = 'No se pudo guardar el pago';
      
      // Manejar diferentes tipos de error
      if (error.response?.status === 400) {
        // Error de validación (cédula no existe)
        errorMessage = error.response.data.detail || 'La cédula no existe en la base de datos';
      } else if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Error de validación con array de errores
          errorMessage = error.response.data.detail.map(err => 
            typeof err === 'object' ? err.msg || JSON.stringify(err) : err
          ).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          // Error de validación como objeto
          errorMessage = JSON.stringify(error.response.data.detail);
        } else {
          // Error como string
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        show: true,
        type: 'error',
        title: 'Error al Guardar',
        message: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cedula: '',
      monto_bs: '',
      monto_usd: '',
      monto_eur: '',
      fecha: new Date().toISOString().split('T')[0],
      concepto: '',
      referencia: '',
      descripcion: ''
    });
    setModalMode('create');
    setSelectedPago(null);
  };

  const handleEdit = (pago) => {
    setFormData({
      cedula: pago.cedula || '',
      monto_bs: pago.monto_bs || '',
      monto_usd: pago.monto_usd || '',
      monto_eur: pago.monto_eur || '',
      fecha: pago.fecha || '',
      concepto: pago.concepto || '',
      referencia: pago.referencia || '',
      descripcion: pago.descripcion || ''
    });
    setModalMode('edit');
    setSelectedPago(pago);
    setShowModal(true);
  };

  const handleDelete = (pago) => {
    setPagoToDelete(pago);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await pagosOficinaService.deletePago(pagoToDelete.id);
      setNotification({
        show: true,
        type: 'success',
        title: 'Pago Eliminado',
        message: 'El pago de oficina se eliminó exitosamente'
      });
      setShowConfirmModal(false);
      setPagoToDelete(null);
      loadPagos();
    } catch (error) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Error al Eliminar',
        message: 'No se pudo eliminar el pago'
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="pagos-oficina-container" data-testid="pagos-oficina">
      <div className="pagos-header">
        <div>
          <h1>Pagos de Oficina</h1>
          <p>Gestión de pagos administrativos del departamento de cobranza</p>
        </div>
        <button 
          onClick={() => setShowModal(true)} 
          className="create-pago-btn" 
          data-testid="create-pago-button"
        >
          + Agregar Pago
        </button>
      </div>

      {/* Filtros */}
      <div className="filters-card">
        <div className="filters-grid">
          <div className="filter-input">
            <label>Búsqueda</label>
            <input
              type="text"
              placeholder="Buscar por cédula, referencia..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              data-testid="search-input"
            />
          </div>

          <div className="filter-input">
            <label>Fecha Desde</label>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(e) => setFilters(prev => ({ ...prev, fecha_desde: e.target.value }))}
              data-testid="fecha-desde-filter"
            />
          </div>

          <div className="filter-input">
            <label>Fecha Hasta</label>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(e) => setFilters(prev => ({ ...prev, fecha_hasta: e.target.value }))}
              data-testid="fecha-hasta-filter"
            />
          </div>

          <div className="filter-input">
            <label>Concepto</label>
            <select
              value={filters.concepto}
              onChange={(e) => setFilters(prev => ({ ...prev, concepto: e.target.value }))}
              data-testid="concepto-filter"
            >
              <option value="">Todos</option>
              <option value="cuota">Cuota</option>
              <option value="inicial">Inicial</option>
              <option value="seguro">Seguro</option>
              <option value="incumplimiento">Incumplimiento</option>
              <option value="finiquito">Finiquito</option>
              <option value="reindexacion">Reindexación</option>
              <option value="reactivacion">Reactivación</option>
              <option value="otros">Otros</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de Pagos */}
      <div className="table-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando pagos...</p>
          </div>
        ) : pagos.length === 0 ? (
          <div className="empty-state">
            <p className="empty-title">No se encontraron pagos</p>
            <p className="empty-text">Comienza agregando el primer pago de oficina</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="pagos-table" data-testid="pagos-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cédula</th>
                  <th>Concepto</th>
                  <th>Monto (EUR)</th>
                  <th>Referencia</th>
                  <th>Usuario</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagos.map((pago) => (
                  <tr key={pago.id} data-testid={`pago-row-${pago.id}`}>
                    <td>{pago.fecha}</td>
                    <td>{pago.cedula}</td>
                    <td className="concepto-cell">
                      <span className={`concepto-badge ${pago.concepto}`}>
                        {pago.concepto}
                      </span>
                    </td>
                    <td>€ {formatCurrency(pago.monto_eur)}</td>
                    <td>{pago.referencia || '-'}</td>
                    <td>{pago.created_by_name || 'Sistema'}</td>
                    <td>
                      <button 
                        className="edit-btn" 
                        onClick={() => handleEdit(pago)}
                        data-testid={`edit-pago-${pago.id}`}
                        title="Editar pago"
                      >
                        ✏️
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDelete(pago)}
                        data-testid={`delete-pago-${pago.id}`}
                        title="Eliminar pago"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para Crear/Editar Pago */}
      {showModal && (
        <div className="modal-overlay" data-testid="create-pago-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Agregar Pago de Oficina' : 'Editar Pago de Oficina'}</h2>
              <button 
                onClick={() => { setShowModal(false); resetForm(); }} 
                className="modal-close"
                data-testid="close-modal-button"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {/* Tasas BCV Header */}
              <div className="bcv-rates-header">
                <div className="rates-info">
                  <h4>💱 Tasas BCV 
                    <button 
                      type="button"
                      onClick={loadBCVRates} 
                      className="refresh-rates" 
                      disabled={loadingRates}
                      style={{
                        marginLeft: '8px',
                        padding: '4px 8px',
                        background: 'rgba(251, 176, 64, 0.2)',
                        border: '1px solid rgba(251, 176, 64, 0.4)',
                        borderRadius: '6px',
                        color: '#fbb040',
                        cursor: loadingRates ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {loadingRates ? '🔄' : '↻'}
                    </button>
                  </h4>
                  <div className="rates-display">
                    <span className="rate-item">
                      USD: {bcvRates.USD?.toString().replace('.', ',') || '250,00'} Bs
                    </span>
                    <span className="rate-item">
                      EUR: {bcvRates.EUR?.toFixed(2).replace('.', ',') || '280,00'} Bs  
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="form-grid">
                <div className="form-field">
                  <label>Cédula *</label>
                  <input
                    type="text"
                    placeholder="12345678"
                    required
                    value={formData.cedula}
                    onChange={(e) => setFormData(prev => ({ ...prev, cedula: e.target.value }))}
                    data-testid="cedula-input"
                  />
                </div>

                <div className="form-field">
                  <label>Monto en Bs</label>
                  <div className="currency-input-group">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={formData.monto_bs}
                      onChange={(e) => handleMonedaChange('monto_bs', e.target.value)}
                      data-testid="monto-bs-input"
                    />
                    <span className="currency-label">Bs</span>
                  </div>
                  <small className="rate-info">
                    {loadingRates ? '⏳' : `1 EUR = ${bcvRates.EUR?.toFixed(2) || '280,00'} Bs`}
                  </small>
                </div>

                <div className="form-field">
                  <label>Monto en USD</label>
                  <div className="currency-input-group">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.monto_usd}
                      onChange={(e) => handleMonedaChange('monto_usd', e.target.value)}
                      data-testid="monto-usd-input"
                    />
                    <span className="currency-label">$</span>
                  </div>
                  <small className="rate-info">
                    {loadingRates ? '⏳' : `1 USD = ${(bcvRates.EUR / bcvRates.USD)?.toFixed(4) || '1,1200'} EUR`}
                  </small>
                </div>

                <div className="form-field">
                  <label>Monto en EUR *</label>
                  <div className="currency-input-group">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      required
                      value={formData.monto_eur}
                      onChange={(e) => handleMonedaChange('monto_eur', e.target.value)}
                      data-testid="monto-eur-input"
                    />
                    <span className="currency-label">€</span>
                  </div>
                  <small className="rate-info required-field">
                    Campo obligatorio (se calcula automáticamente)
                  </small>
                </div>

                <div className="form-field">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    required
                    value={formData.fecha}
                    onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                    data-testid="fecha-input"
                  />
                </div>

                <div className="form-field">
                  <label>Concepto *</label>
                  <select
                    required
                    value={formData.concepto}
                    onChange={(e) => setFormData(prev => ({ ...prev, concepto: e.target.value }))}
                    data-testid="concepto-select"
                  >
                    <option value="">Selecciona concepto</option>
                    <option value="cuota">Cuota</option>
                    <option value="inicial">Inicial</option>
                    <option value="seguro">Seguro</option>
                    <option value="incumplimiento">Incumplimiento</option>
                    <option value="finiquito">Finiquito</option>
                    <option value="reindexacion">Reindexación</option>
                    <option value="reactivacion">Reactivación</option>
                    <option value="otros">Otros</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Referencia</label>
                  <input
                    type="text"
                    placeholder="Número de referencia"
                    value={formData.referencia}
                    onChange={(e) => setFormData(prev => ({ ...prev, referencia: e.target.value }))}
                    data-testid="referencia-input"
                  />
                </div>

                <div className="form-field form-field-wide">
                  <label>Descripción</label>
                  <textarea
                    placeholder="Descripción adicional del pago..."
                    rows={3}
                    value={formData.descripcion}
                    onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                    data-testid="descripcion-textarea"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="cancel-btn"
                  data-testid="cancel-button"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={saving}
                  data-testid="submit-button"
                >
                  {saving ? 'Guardando...' : (modalMode === 'create' ? 'Guardar Pago' : 'Actualizar Pago')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmación para Eliminar */}
      {showConfirmModal && pagoToDelete && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title="Eliminar Pago"
          message={`¿Estás seguro de que deseas eliminar el pago de €${formatCurrency(pagoToDelete.monto_eur)} para la cédula ${pagoToDelete.cedula}?`}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowConfirmModal(false);
            setPagoToDelete(null);
          }}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />
      )}

      {/* Modal de Notificación */}
      <NotificationModal
        isOpen={notification.show}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ show: false, type: 'success', title: '', message: '' })}
      />
    </div>
  );
};

export default PagosOficina;